import React, { FC } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { DevTool } from "@hookform/devtools";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  CreateUpdateRevenueAndExpenseDto,
  CurrentCompanyInfoDto,
  CurrentCompanySettingsDto,
} from "@green0meter/api-client";

import Section from "./components/Section";
import ThemeSettings from "./components/ThemeSettings";
import NaceSettings from "./components/NaceSettings";
import { OrganizationSettingsFormValues } from "./OrganizationSettings.types";
import CurrencySettings from "./components/CurrencySettings";
import RevenueAndExpensesSettings from "./components/RevenueAndExpensesSettings";
import CustomLogo from "./components/CustomLogo";
import CompanyInfo from "./components/CompanyInfo";
import CompanyAddress from "./components/CompanyAddress";

import {
  useCreateUpdateRevenueAndExpense,
  useDeleteRevenueAndExpense,
  useUpdateCurrentCompanySettings,
  useGetRevenueAndExpenses,
} from "@api";
import { Box, Button, Stack } from "@components/base";
import { useUserStore } from "@store/user";

interface OrganizationSettings {
  currentCompanyInfo: CurrentCompanyInfoDto;
  currentCompanySettings: CurrentCompanySettingsDto;
}

const OrganizationSettings: FC<OrganizationSettings> = ({
  currentCompanyInfo,
  currentCompanySettings,
}) => {
  const { user } = useUserStore();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const { revenueAndExpenses } = useGetRevenueAndExpenses(user, {
    companyId: currentCompanyInfo.id,
  });
  const {
    updateCurrentCompanySettings,
    updateCurrentCompanySettingsEnabled,
    isPendingUpdateCurrentCompanySettings,
  } = useUpdateCurrentCompanySettings(user);
  const {
    createUpdateRevenueAndExpense,
    createUpdateRevenueAndExpenseEnabled,
    isPendingCreateUpdateRevenueAndExpense,
  } = useCreateUpdateRevenueAndExpense(user);
  const {
    deleteRevenueAndExpense,
    deleteRevenueAndExpenseEnabled,
    isPendingDeleteRevenueAndExpense,
  } = useDeleteRevenueAndExpense(user);

  const form = useForm<OrganizationSettingsFormValues>({
    defaultValues: {
      naceCodeArea: currentCompanySettings.naceDivision?.naceSectionId ?? "",
      naceCodeIndustry: currentCompanySettings.naceDivision?.id ?? "",
      currency: currentCompanySettings.currency?.id ?? "",
    },
  });

  const onSubmit: SubmitHandler<OrganizationSettingsFormValues> = async (data) => {
    if (createUpdateRevenueAndExpenseEnabled && deleteRevenueAndExpenseEnabled) {
      const toCreateOrUpdate = data.revenueAndExpenses.filter((item) => {
        const existingItem =
          revenueAndExpenses &&
          revenueAndExpenses.find((fetchedItem) => fetchedItem.id === item.id);

        return (
          !existingItem ||
          existingItem.year !== item.year ||
          existingItem.revenue !== item.revenue ||
          existingItem.opex !== item.opex ||
          existingItem.capex !== item.capex
        );
      });

      const toDelete =
        revenueAndExpenses &&
        revenueAndExpenses.filter(
          (fetchedItem) =>
            !data.revenueAndExpenses.some((item) => item.id === fetchedItem.id),
        );
      const payload: CreateUpdateRevenueAndExpenseDto[] = toCreateOrUpdate.map(
        (item) => {
          return {
            companyId: currentCompanyInfo.id,
            year: item.year,
            revenue: item.revenue ?? 0,
            opex: item.opex ?? 0,
            capex: item.capex ?? 0,
          };
        },
      );

      createUpdateRevenueAndExpense(payload);

      // in case there are more items to delete
      if (toDelete && toDelete?.length > 0) {
        await Promise.all(toDelete.map((item) => deleteRevenueAndExpense(item.id)));
      }
    }

    if (updateCurrentCompanySettingsEnabled) {
      updateCurrentCompanySettings({
        currencyId: data.currency,
        naceDivisionId: data.naceCodeIndustry,
      });
    }

    // TODO: propagate errors from api
    enqueueSnackbar(t("organizationSettings.settingsSaved"), {
      variant: "success",
    });
  };

  return (
    <Box mt={3}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Stack spacing={3}>
          {/* TODO: integrate as another proper section or delete? */}
          <Section
            title={t("organizationSettings.companyInfoTitle")}
            description={t("organizationSettings.companyInfoDescription")}
            setting={<CompanyInfo />}
          />
          <Section
            title={t("organizationSettings.companyAddress.title")}
            description={t("organizationSettings.companyAddress.description")}
            setting={<CompanyAddress />}
          />
          <Section
            title={t("organizationSettings.companyLogo.title")}
            description={t("organizationSettings.companyLogo.description")}
            setting={<CustomLogo />}
          />
          {/* TODO: save theme settings on "save" click, currently it's saved onChange */}
          <Section
            title={t("organizationSettings.theme.title")}
            description={t("organizationSettings.theme.description")}
            setting={<ThemeSettings />}
          />
          <Section
            title={t("organizationSettings.nace.title")}
            description={t("organizationSettings.nace.description")}
            setting={<NaceSettings form={form} />}
          />
          <Section
            title={t("organizationSettings.currency.title")}
            description={t("organizationSettings.currency.description")}
            setting={<CurrencySettings form={form} />}
          />
          {createUpdateRevenueAndExpenseEnabled &&
            deleteRevenueAndExpenseEnabled && (
              <Section
                title={t("organizationSettings.revenueAndExpenses.title")}
                description={t(
                  "organizationSettings.revenueAndExpenses.description",
                )}
                setting={
                  <RevenueAndExpensesSettings
                    form={form}
                    companyId={currentCompanyInfo.id}
                  />
                }
              />
            )}
          <Box>
            <Button
              isLoading={
                isPendingUpdateCurrentCompanySettings ||
                isPendingCreateUpdateRevenueAndExpense ||
                isPendingCreateUpdateRevenueAndExpense ||
                isPendingDeleteRevenueAndExpense
              }
              size="large"
              type="submit"
            >
              {t("common.save")}
            </Button>
          </Box>
        </Stack>
      </form>
      <DevTool control={form.control} />
    </Box>
  );
};

export default OrganizationSettings;
